/**
 * Centralized Data Service Layer
 * Server-side only - No localStorage caching for entity data
 */

import axios from 'axios';
import { ENV_CONFIG } from '../../config/environment';

// Create axios instance
const api = axios.create({
  baseURL: ENV_CONFIG.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Encoding': 'gzip, deflate, br',
  },
  maxRedirects: 0,
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Custom error classes
class DataServiceError extends Error {
  constructor(message, code, originalError) {
    super(message);
    this.name = 'DataServiceError';
    this.code = code;
    this.originalError = originalError;
  }
}

class NetworkError extends DataServiceError {
  constructor(message, originalError) {
    super(message, 'NETWORK_ERROR', originalError);
    this.name = 'NetworkError';
  }
}

class DataService {
  constructor() {
    this.cache = new Map(); // In-memory cache only
    this.pendingRequests = new Map();
    this.cacheDuration = ENV_CONFIG.CACHE_DURATION || 60000; // 1 minute default
  }

  /**
   * Load data from API with in-memory caching only
   * @param {string} dataType - The type of data (e.g., 'centers', 'vaccines')
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise<Array>} The data array
   */
  async loadData(dataType, forceRefresh = false) {
    try {
      // Check in-memory cache first
      if (!forceRefresh) {
        const cached = this.cache.get(dataType);
        if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
          return cached.data;
        }
      }

      // Prevent duplicate requests
      if (this.pendingRequests.has(dataType)) {
        return await this.pendingRequests.get(dataType);
      }

      // Fetch from API
      const requestPromise = api.get(`/${dataType}`)
        .then(response => {
          const data = response.data?.data || response.data;
          this.cache.set(dataType, { data, timestamp: Date.now() });
          this.pendingRequests.delete(dataType);
          return data;
        })
        .catch(error => {
          this.pendingRequests.delete(dataType);
          throw error;
        });

      this.pendingRequests.set(dataType, requestPromise);
      return await requestPromise;

    } catch (error) {
      throw new NetworkError(`Failed to load ${dataType}`, error);
    }
  }

  /**
   * Create a new item via API
   * @param {string} dataType - The type of data
   * @param {Object} item - The item to create
   * @returns {Promise<Object>} Created item
   */
  async createItem(dataType, item) {
    try {
      const response = await api.post(`/${dataType}`, item);
      const newItem = response.data?.data || response.data;

      // Clear cache to force refresh on next load
      this.clearCache(dataType);

      return newItem;
    } catch (error) {
      throw new NetworkError(`Failed to create ${dataType}`, error);
    }
  }

  /**
   * Update an existing item via API
   * @param {string} dataType - The type of data
   * @param {number|string} id - The item ID
   * @param {Object} updates - The updates to apply
   * @returns {Promise<Object>} Updated item
   */
  async updateItem(dataType, id, updates) {
    try {
      const response = await api.put(`/${dataType}/${id}`, updates);
      const updatedItem = response.data?.data || response.data;

      // Clear cache to force refresh on next load
      this.clearCache(dataType);

      return updatedItem;
    } catch (error) {
      throw new NetworkError(`Failed to update ${dataType}`, error);
    }
  }

  /**
   * Delete an item via API
   * @param {string} dataType - The type of data
   * @param {number|string} id - The item ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteItem(dataType, id) {
    try {
      await api.delete(`/${dataType}/${id}`);

      // Clear cache to force refresh on next load
      this.clearCache(dataType);

      return true;
    } catch (error) {
      throw new NetworkError(`Failed to delete ${dataType}`, error);
    }
  }

  /**
   * Clear in-memory cache for a specific data type
   * @param {string} dataType - The type of data
   */
  clearCache(dataType) {
    this.cache.delete(dataType);
  }

  /**
   * Clear all in-memory cache
   */
  clearAllCache() {
    this.cache.clear();
  }

  /**
   * Prefetch data in background
   * @param {string} dataType - The type of data to prefetch
   */
  prefetch(dataType) {
    setTimeout(() => this.loadData(dataType), 0);
  }

  /**
   * Prefetch multiple data types
   * @param {Array<string>} dataTypes - Array of data types to prefetch
   */
  prefetchMultiple(dataTypes) {
    dataTypes.forEach(type => this.prefetch(type));
  }

  /**
   * Get multiple data types in parallel
   * @param {Array<string>} dataTypes - Array of data types to fetch
   * @returns {Promise<Object>} Object with data for each type
   */
  async loadMultiple(dataTypes) {
    try {
      const promises = dataTypes.map(type => this.loadData(type));
      const results = await Promise.all(promises);

      const data = {};
      dataTypes.forEach((type, index) => {
        data[type] = results[index];
      });

      return data;
    } catch (error) {
      console.error("Error loading multiple data types:", error);
      throw error;
    }
  }
}

// Export singleton instance
const dataService = new DataService();

export default dataService;

// Named exports for convenience
export const {
  loadData,
  createItem,
  updateItem,
  deleteItem,
  clearCache,
  clearAllCache,
  loadMultiple,
} = dataService;
